import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Wifi, MapPin, ArrowRight, Zap, Shield, Check, PhoneCall } from 'lucide-react';

const providers = [
  { name: 'Telekom', note: 'Glasfaser & DSL', speed: 'bis 1000 Mbit/s' },
  { name: 'Vodafone', note: 'Kabel & DSL', speed: 'bis 1000 Mbit/s' },
  { name: 'O2', note: 'DSL', speed: 'bis 250 Mbit/s' },
  { name: '', note: 'DSL & Glasfaser', speed: 'bis 1000 Mbit/s' },
];

const faqs = [
  {
    q: 'Welche Internet-Anbieter gibt es in Völklingen und dem Saarland?',
    a: 'In Völklingen und dem Saarland sind Telekom (DSL/Glasfaser), Vodafone (Kabel/DSL), O2 (DSL) (DSL/Glasfaser) verfügbar. Die genaue Verfügbarkeit hängt von Ihrer Adresse ab. Wir prüfen das kostenlos für Sie.',
  },
  {
    q: 'Welcher Internet-Anbieter ist im Saarland am schnellsten?',
    a: 'Telekom und Vodafone bieten im Saarland die größte Abdeckung mit bis zu 1000 Mbit/s Glasfaser- und Kabelanschlüssen. In Völklingen ist Glasfaser in vielen Stadtteilen bereits verfügbar.',
  },
  {
    q: 'Wie schnell kann ich im Saarland Internet bekommen?',
    a: 'In vielen Bereichen des Saarlandes sind bereits Glasfaseranschlüsse mit bis zu 1000 Mbit/s verfügbar. DSL-Anschlüsse sind flächendeckend mit bis zu 250 Mbit/s möglich.',
  },
  {
    q: 'Kann ich den Internet-Anbieter im Saarland einfach wechseln?',
    a: 'Ja! Wir übernehmen die komplette Abwicklung des Anbieterwechsels für Sie – kostenlos. Einfach bei uns melden, Anbieter vergleichen und wir kümmern uns um alles.',
  },
  {
    q: 'Gibt es in Völklingen günstiges Internet ohne lange Laufzeit?',
    a: 'Ja, mehrere Anbieter bieten monatlich kündbare Internet-Verträge an. Diese sind etwas teurer, bieten aber maximale Flexibilität. Wir vergleichen alle Optionen für Sie.',
  },
];

const InternetSaarlandPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Internet Anbieter Saarland & Völklingen vergleichen | Easy Vertrag</title>
        <meta name="description" content="Alle Internet-Anbieter im Saarland vergleichen - DSL, Glasfaser & Kabel in Völklingen, Saarbrücken und der Saar-Region. Telekom, Vodafone, O2. Kostenlose Beratung - jetzt wechseln!" />
        <meta name="keywords" content="Internet Anbieter Saarland, DSL Völklingen, Glasfaser Saarland, Internet Völklingen, Internet Saarbrücken, DSL Tarife Saarland, schnelles Internet Saar" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/internet-anbieter-saarland" />
        <meta property="og:title" content="Internet Anbieter Saarland vergleichen | Easy Vertrag Völklingen" />
        <meta property="og:description" content="DSL, Glasfaser & Kabel im Saarland vergleichen. Persönliche Beratung in Völklingen." />
        <meta property="og:url" content="https://easy4vertrag.de/internet-anbieter-saarland" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Internet Anbieter Saarland", "item": "https://easy4vertrag.de/internet-anbieter-saarland"}
            ]
          }
        `}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Beratung in Völklingen · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Internet Anbieter im Saarland vergleichen
            </h1>
            <p className="text-xl text-teal-100 mb-4">
              DSL, Glasfaser und Kabel-Internet in Völklingen, Saarbrücken und dem gesamten Saarland. Wir vergleichen alle Anbieter kostenlos für Sie.
            </p>
            <p className="text-teal-200 mb-8 text-base">
              Persönliche Beratung in Völklingen · +49 179 7473491
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/internet-zuhause"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50 transition-colors"
              >
                Internet jetzt vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/491797473491"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-teal-700 transition-colors"
              >
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Grid */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Internet-Anbieter im Saarland im Überblick
            </h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Alle verfügbaren Anbieter in Völklingen und der Saar-Region – mit aktuellen Tarifen und Konditionen.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {providers.map((p, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 flex items-center gap-5">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg">{p.name}</h3>
                    <p className="text-gray-500 text-sm">{p.note}</p>
                    <span className="inline-block mt-1 text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">{p.speed}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-teal-600 text-white rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">Verfügbarkeit in Ihrer Adresse prüfen</h3>
              <p className="text-teal-100 mb-6 max-w-lg mx-auto">
                Welche Internet-Anbieter sind an Ihrer Adresse im Saarland verfügbar? Unser Vergleich zeigt alle Optionen sofort.
              </p>
              <Link
                to="/internet-zuhause"
                className="inline-flex items-center px-8 py-3 bg-white text-teal-700 font-bold rounded-lg hover:bg-teal-50 transition-colors"
              >
                Jetzt Verfügbarkeit prüfen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Warum Internet-Vergleich über Easy Vertrag in Völklingen?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <MapPin className="w-6 h-6 text-teal-600" />, title: 'Lokal & persönlich', text: 'Wir kennen die Netzabdeckung im Saarland und beraten Sie gezielt für Ihre Adresse.' },
                { icon: <Shield className="w-6 h-6 text-teal-600" />, title: 'Kostenloser Wechselservice', text: 'Wir übernehmen die komplette Abwicklung – Kündigung, Anmeldung, alles.' },
                { icon: <Zap className="w-6 h-6 text-teal-600" />, title: 'Aktuelle Tarife', text: 'Immer die neuesten Angebote und Sonderaktionen aller Anbieter im Vergleich.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 text-center border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Internet-Beratung in ganz Saarland
            </h2>
            <p className="text-gray-500 text-center mb-10">
              Wir beraten Kunden aus dem gesamten Saarland - persönlich in Völklingen oder per Telefon und WhatsApp.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                'Völklingen', 'Saarbrücken', 'Saarlouis', 'Dillingen',
                'Wadgassen', 'Merzig', 'Neunkirchen', 'Homburg',
                'St. Ingbert', 'Blieskastel', 'Püttlingen', 'Quierschied',
              ].map((city, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm">
                  <Check className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span className="text-gray-700">{city}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Häufige Fragen zu Internet im Saarland
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-teal-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Internet-Anbieter im Saarland wechseln</h2>
            <p className="text-teal-100 mb-2">Easy Vertrag · Bismarck Straße 31 · 66333 Völklingen</p>
            <p className="text-teal-200 text-sm mb-8">Mo–Fr 10:00–20:00 Uhr · Sa 10:00–18:00 Uhr</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+491797473491"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50 transition-colors"
              >
                <PhoneCall className="w-5 h-5 mr-2" />
                Jetzt anrufen
              </a>
              <Link
                to="/internet-zuhause"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-teal-700 transition-colors"
              >
                Tarife vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternetSaarlandPage;
